import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { ITASCA_URL } from '@/constants/urls';
import { SchoolcraftSoilResponse } from '@/endpoints/schoolcraft/soil/getSoil';
import { AcresFeature } from '@/models/features';
import {
  CropLand,
  CropLandWithYears,
  CropLandYear,
  NASSData,
  PastureLand,
  PastureLandWithYears,
  PastureLandYear,
  Yields,
} from '@/models/Nass';

import { SoilResponse } from '../../models/soils';
import {
  guessActiveKey,
  ISoilAverages,
  SoilIndex,
} from '../../utils/uniqueEndpoints';

type Boundary = AcresFeature | null;

export type SoilTransformedV3Response = {
  feature_collection: SchoolcraftSoilResponse;
  soilAverages: ISoilAverages;
  soilScoreIndex: SoilIndex;
};

export interface GetSoilQuery {
  boundary: Boundary;
  combinedBoundaries: AcresFeature[];
}
type Scores = {
  [key in SoilIndex]?: IScore;
};
export interface IScore {
  average: number;
  key: SoilIndex;
}
export interface SoilResponseQuery {
  soilAverages: ISoilAverages;
  soilData: SoilResponse;
  soilScoreIndex: SoilIndex;
  scores?: Scores;
}

export interface GetNASSCountyQuery {
  data: { boundary: Boundary };
  url: string;
}

export interface GetNASSCountyResponseQuery {
  data: NASSData;
  state: string | null;
  county: string | null;
  rentRecordCropLand: Record<number, CropLandYear>;
  rentRecordPastureland: Record<number, PastureLandYear>;
}

export const itascaApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: `${ITASCA_URL}`,
    prepareHeaders: (headers) => headers,
  }),
  endpoints: (builder) => ({
    getNASSCounty: builder.query<
      GetNASSCountyResponseQuery,
      GetNASSCountyQuery
    >({
      query: ({ data, url }) => {
        return {
          body: data,
          credentials: 'include',
          method: 'POST',
          url,
        };
      },
      transformResponse: (response: NASSData) => {
        const { state, county } = response;
        const cropland: CropLand = {};
        const croplandYears: string[] = [];
        const pastureland: PastureLand = {};
        const pasturelandYears: string[] = [];

        const { rents, yields } = response;

        if (rents)
          Object.keys(rents).forEach((year) => {
            if (rents[year].cropland) {
              cropland[year] = rents[year].cropland;
              croplandYears.push(year);
            }
            if (rents[year].pastureland) {
              pastureland[year] = rents[year].pastureland;
              pasturelandYears.push(year);
            }
          });

        const rentRecordCropLand: CropLand = {
          ...cropland,
        };
        const rentRecordPastureland: PastureLand = {
          ...pastureland,
        };

        const newYields: Yields = {};

        if (yields)
          Object.keys(yields).forEach((year) => {
            Object.keys(yields[year]).forEach((cropType) => {
              const handledCropType = `${cropType}`.toLowerCase();
              if (!newYields[handledCropType]) newYields[handledCropType] = {};
              const crop = yields[year][cropType];
              const val = crop.production.AVG;
              if (val)
                newYields[handledCropType][year] = {
                  delta: val.yield_delta,
                  value: val.yield,
                };
              newYields[handledCropType].unit = crop.yield_unit_desc;
            });
          });

        return {
          county,
          data: {
            county,
            rents: {
              cropland: {
                ...rentRecordCropLand,
                years: croplandYears,
              } as CropLandWithYears,
              pastureland: {
                ...rentRecordPastureland,
                years: pasturelandYears,
              } as PastureLandWithYears,
            },
            state,
            yields: newYields,
          },
          rentRecordCropLand,
          rentRecordPastureland,
          response,
          state,
        };
      },
    }),
    getSoil: builder.query<SoilTransformedV3Response, GetSoilQuery>({
      query: (data) => ({
        body: {
          boundary: data.boundary,
        },
        credentials: 'include',
        headers: {
          Accept: 'application/json;version=3',
        },
        method: 'POST',
        url: '/soil/productivity/',
      }),
      transformResponse: (response: SoilResponse) => {
        const soilIndex = guessActiveKey(response);

        const soilAverages = {
          CPI: response.CPI__average,
          CSR2: response.CSR2__average,
          NCCPI: response.NCCPI__average,
          PI: response.PI__average,
          STORIE: response.STORIE__average,
          WAPI: response.WAPI__average,
        } as ISoilAverages;

        return {
          feature_collection: response.feature_collection,
          soilAverages,
          soilScoreIndex: soilIndex,
        };
      },
    }),
  }),

  reducerPath: 'itascaserverApi',
});
export const { useGetNASSCountyQuery, useGetSoilQuery } = itascaApi;
